import React from 'react';
import { Tooltip, Radio, RadioChangeEvent } from 'antd';
import { get } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { setDataType } from '@store/viewerSessions/actions';
import featureNames from '@utils/featureToggle/constants';
import { moreThanViewerDrillDownLimit } from '@utils/granularity';
import viewerSessionsSelector from '@store/viewerSessions/selectors';
import commonSelector, { realTimeModeOnTrendsSelector } from '@store/common/selectors';
import dimensionContainerSelector from '@store/dimensionContainer/selectors';
import { chartTypeSelector } from '@store/trends/birdChart/selectors';
import { isDrilldownFeatureByMetric } from '@components/viewer-sessions/utils';
import { CONST_STATE } from '@store/viewerSessions/reducer';
import { DISTRIBUTION_METRICS_MAPPING } from '@utils/metrics/distribution';
import { isErrorCodeByMetric } from '@utils/dimension/errorCodeHelper';
import { DataType, DisabledUnitToggle, UnitToggleWrapper } from '../styled';

const SWITCH_CHOICES = [
  { label: 'Top Trends', value: 'dimension' },
  { label: 'Viewer Sessions', value: 'sessions' },
];

const LAST_15_MINS_MESSAGE = 'Not supported for last 15 minutes';
const MORE_THAN_LIMIT_TOOLTIP_TITLE = 'The start time is over 31 days.';
const FEATURE_UNAVAILABLE_TEXT =
  'Viewer Sessions allows you to see individual viewer streaming sessions and respective timeline of events.';
const CONTACT_SUPPORT = 'To enable this feature, please contact your Conviva Account Team.';

const FEATURE_UNAVAILABLE = () => (
  <>
    <p>{FEATURE_UNAVAILABLE_TEXT}</p>
    <p>{CONTACT_SUPPORT}</p>
  </>
);
const IsVDEnabled = () => {
  const drillDownFlag = get(useSelector(commonSelector), [
    'features',
    featureNames.VIEWER_DRILLDOWN,
  ]);
  return drillDownFlag;
};

const IsVDEnabledRestMetrics = () => {
  const drillDownRestMetricsFlag = get(useSelector(commonSelector), [
    'features',
    featureNames.VIEWER_DRILLDOWN_REST_METRICS,
  ]);
  return drillDownRestMetricsFlag;
};

export const IsDrilldownDisabled = (metric, forceRender) => {
  const drillDownRestMetricsFlag = IsVDEnabledRestMetrics();

  const diagnosisChartType = useSelector(chartTypeSelector);
  const { showDiagChart } = useSelector(dimensionContainerSelector);
  const distributionMetricsFlag = get(useSelector(commonSelector), [
    'features',
    featureNames.VIEWER_DRILLDOWN_DISTRIBUTION,
  ]);

  const drillDownFeatureFunc = drillDownRestMetricsFlag
    ? isDrilldownFeatureByMetric
    : isErrorCodeByMetric;
  const checks = [
    diagnosisChartType === 'bar', // diagnostics is bar chart
    !forceRender && !showDiagChart, // not showing diagnostics chart
    !drillDownFeatureFunc(metric), // the metric doesn't support drilldown
    !!DISTRIBUTION_METRICS_MAPPING[metric] && !distributionMetricsFlag,
  ];
  return checks.some((v) => v);
};

export const noDrilldown = (metric, forceRender) => {
  const drillDownFlag = IsVDEnabled();
  const isVDDisabled = IsDrilldownDisabled(metric, forceRender);
  const checks = [
    !drillDownFlag, // no drilldown feature
    isVDDisabled,
  ];
  return checks.some((v) => v);
};

const SwitchRender = (selected, handleToggle) => (
  <Radio.Group
    value={selected}
    onChange={handleToggle}
    options={SWITCH_CHOICES}
    optionType="button"
  />
);
// eslint-disable-next-line react/display-name
export default ({ interval, metric, viewerDrillDownKey, forceRender }) => {
  const dispatch = useDispatch();
  const { dataType } = get(useSelector(viewerSessionsSelector), viewerDrillDownKey, CONST_STATE);
  const isTrends = dataType === 'dimension';
  const liveModeSelected = useSelector(realTimeModeOnTrendsSelector);

  const handleToggle = ({ target: { value } }: RadioChangeEvent) => {
    dispatch(setDataType(value, viewerDrillDownKey));
  };

  const getToolTipData = () => {
    if (liveModeSelected) {
      return LAST_15_MINS_MESSAGE;
    }
    return featureUnavailable ? <FEATURE_UNAVAILABLE /> : MORE_THAN_LIMIT_TOOLTIP_TITLE;
  };

  const noRender = IsDrilldownDisabled(metric, forceRender) || window.conviva.module !== 'EI';
  const featureUnavailable = !(IsVDEnabled() && IsVDEnabledRestMetrics());
  const viewerSessionDisabled = moreThanViewerDrillDownLimit(interval.from);
  const disabledToggle = !!(viewerSessionDisabled || featureUnavailable || liveModeSelected);
  const selected = isTrends ? SWITCH_CHOICES[0].value : SWITCH_CHOICES[1].value;

  const toolTipText = getToolTipData();

  if (noRender) {
    return <DataType readonly>Top Trends</DataType>;
  }

  if (disabledToggle) {
    return (
      <Tooltip title={toolTipText}>
        <DisabledUnitToggle>
          {SwitchRender(SWITCH_CHOICES[0].value, handleToggle)}
        </DisabledUnitToggle>
      </Tooltip>
    );
  }

  return <UnitToggleWrapper>{SwitchRender(selected, handleToggle)}</UnitToggleWrapper>;
};
